
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/contexts/useAuth';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { estimateFormSchema, EstimateFormValues } from '../types/estimateFormTypes';
import { useImpersonationGuard } from '@/hooks/useImpersonationGuard';

export const useEstimateForm = (estimateId?: string) => {
  const { user, profile } = useAuth();
  const { guardWrite } = useImpersonationGuard();
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(!estimateId); // If no estimateId, data is already "loaded"
  const isEditMode = !!estimateId;

  const form = useForm<EstimateFormValues>({
    resolver: zodResolver(estimateFormSchema),
    defaultValues: {
      title: '',
      description: '',
      customer_id: '',
      status: 'draft',
      tax_percentage: 15,
      tax_inclusive: false,
      special_instructions: '',
      payment_terms: '',
      validity_period: undefined, // Will be set later
      use_separate_site_address: false,
      site_street_address: '',
      site_suburb: '',
      site_city: '',
      site_province_state: '',
      site_postal_code: '',
    },
  });

  // Fetch estimate data if in edit mode
  useEffect(() => {
    const fetchEstimate = async () => {
      if (estimateId) {
        setIsLoading(true);
        setIsDataLoaded(false);
        try {
          const { data, error } = await supabase
            .from('estimates')
            .select('*')
            .eq('id', estimateId)
            .single();

          if (error) throw error;

          form.reset({
            title: data.title,
            description: data.description || '',
            customer_id: data.customer_id || '',
            status: data.status as 'draft' | 'sent' | 'approved' | 'rejected' | 'expired',
            tax_percentage: data.tax_percentage || 15,
            tax_inclusive: data.tax_inclusive || false,
            special_instructions: data.special_instructions || '',
            payment_terms: data.payment_terms || '',
            validity_period: data.validity_period || undefined,
            use_separate_site_address: data.use_separate_site_address || false,
            site_street_address: data.site_street_address || '',
            site_suburb: data.site_suburb || '',
            site_city: data.site_city || '',
            site_province_state: data.site_province_state || '',
            site_postal_code: data.site_postal_code || '',
          });
          setIsDataLoaded(true);
        } catch (error) {
          console.error('Error fetching estimate:', error);
          toast.error('Failed to load estimate');
          setIsDataLoaded(true); // Still mark as loaded to show form with error
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchEstimate();
  }, [estimateId]); // Removed 'form' to prevent re-runs

  const handleSubmit = async (data: EstimateFormValues, onSuccess: (estimateId: string) => void) => {
    if (!guardWrite('create or edit estimates')) return;
    console.log('[useEstimateForm] Starting handleSubmit with data:', data);
    console.log('[useEstimateForm] User object:', user);
    console.log('[useEstimateForm] Profile object:', profile);
    
    const companyId = profile?.company_id;
    
    if (!companyId) {
      console.error('[useEstimateForm] User company ID not found in profile');
      console.error('[useEstimateForm] Available profile data:', profile);
      toast.error('User company ID not found');
      return;
    }

    console.log('[useEstimateForm] User company ID found:', companyId);
    setIsLoading(true);

    try {
      const estimateData = {
        title: data.title,
        description: data.description || null,
        customer_id: data.customer_id === 'no-customer' ? null : data.customer_id || null,
        status: data.status,
        tax_percentage: data.tax_percentage,
        tax_inclusive: data.tax_inclusive,
        // These fields will be null initially and filled later
        special_instructions: data.special_instructions || null,
        payment_terms: data.payment_terms || null,
        validity_period: data.validity_period || null,
        company_id: companyId,
        use_separate_site_address: data.use_separate_site_address,
        site_street_address: data.use_separate_site_address ? data.site_street_address || null : null,
        site_suburb: data.use_separate_site_address ? data.site_suburb || null : null,
        site_city: data.use_separate_site_address ? data.site_city || null : null,
        site_province_state: data.use_separate_site_address ? data.site_province_state || null : null,
        site_postal_code: data.use_separate_site_address ? data.site_postal_code || null : null,
      };

      console.log('[useEstimateForm] Prepared estimate data:', estimateData);

      let estimate;
      if (isEditMode) {
        console.log('[useEstimateForm] Updating existing estimate with ID:', estimateId);
        // Update existing estimate
        const { data: updatedEstimate, error } = await supabase
          .from('estimates')
          .update(estimateData)
          .eq('id', estimateId)
          .select()
          .single();

        if (error) {
          console.error('[useEstimateForm] Error updating estimate:', error);
          throw error;
        }
        estimate = updatedEstimate;
        console.log('[useEstimateForm] Successfully updated estimate:', estimate);
        toast.success('Estimate updated successfully');
      } else {
        console.log('[useEstimateForm] Creating new estimate');
        // Create new estimate
        const { data: newEstimate, error } = await supabase
          .from('estimates')
          .insert({
            ...estimateData,
            created_by: user.id,
            version: 1,
          })
          .select()
          .single();

        if (error) {
          console.error('[useEstimateForm] Error creating estimate:', error);
          throw error;
        }
        estimate = newEstimate;
        console.log('[useEstimateForm] Successfully created estimate:', estimate);
        toast.success('Estimate created successfully');
      }

      if (estimate) {
        console.log('[useEstimateForm] Calling onSuccess with estimate ID:', estimate.id);
        onSuccess(estimate.id);
      } else {
        console.error('[useEstimateForm] No estimate returned from database operation');
      }
    } catch (error) {
      console.error('[useEstimateForm] Error saving estimate:', error);
      toast.error('Failed to save estimate');
    } finally {
      setIsLoading(false);
      console.log('[useEstimateForm] handleSubmit completed');
    }
  };

  return {
    form,
    isLoading,
    isEditMode,
    isDataLoaded,
    handleSubmit,
  };
};
